import { useEffect, useMemo, useState } from "react";
import { useAutoSuggestionsStore } from "../../toolympus/components/primitives/AutoSuggests";
import { useLoadedData } from "../../toolympus/hooks/useLoadedData";
import { createSelectSchema, FieldType, mergeSchema, Schema, useSingleSchema } from "../../toolympus/hooks/useSchema";
import { useSmartList } from "../../toolympus/hooks/useSmartList"
import { Project, ProjectStatus } from "./types";
import { ProjectStageApiPath } from "./useProjectStages";
import { ProjectStatusApiPath } from "./useProjectStatuses";
import { useStatusDates } from "../Settings/useStatusDates";
import { DictionaryRecord } from "../../toolympus/hooks/useDictionaries";
import { useBrowserStoredValue } from "../../toolympus/hooks/useBrowserStoredValue";
import { useEditItem2 } from "../../toolympus/api/useNewItem";
import { apiFetch } from "../../toolympus/api/core";
import { ProjectStatusEdit } from "./ProjectStatusEditDialog";
import { useProjectStates } from "./useProjectStates";

export const CuratorsSuggestionsKey = "curators";
export const MembersSuggestionsKey = "members";

const ProjectApiPath = "/api/project/project";

const OverloadedMemberHeroCount = 3;

const useStateFilterOptions = () => {
  const projectStates = useProjectStates();

  const options = useMemo(() => {
    const rs = projectStates.records;
    const toStates = (rx: DictionaryRecord[]) => rx.map(r => r.code).join(",");
    return [
      { label: "Фокусные", code: "focus", states: toStates(rs.filter(r => r.extra?.is_focus)) },
      { label: "Обратить внимание", code: "pay-attention", states: "pay-attention" },
      { label: "В работе", code: "inprogress", states: toStates(rs.filter(r => r.extra?.is_inprogress)) },
      { label: "На холде", code: "hold", states: toStates(rs.filter(r => r.extra?.is_hold)) },
      { label: "Кроме завершенных", code: "unfinished", states: toStates(rs.filter(r => !r.extra?.is_finished)) },
      { label: "Завершенные", code: "finished", states: toStates(rs.filter(r => r.extra?.is_finished)) },
      { label: "Все", code: "all", states: "" },
    ]
  }, [projectStates.records]);

  return options;
}

const SortingOptions = [
  { label: "по статусу", code: "status" },
  { label: "по дедлайну", code: "deadline" },
  { label: "по коду", code: "code" },
  { label: "по названию", code: "title" },
]

const ActorsFilterSchema: Schema = {
  actor_kind: createSelectSchema([
    { value: "hero", label: "Герой" },
    { value: "support", label: "Поддержка" },
  ], { label: "Фильтр"}),
}

const EmptyStrings: string[] = [];

const useActorsFilter = (heroes?: string[]) => {
  const [actorsFilter,setActorsFilter] = useState<{ actor_kind?: string, actor?: string } | null>(null);
  const editActorsFilter = useEditItem2<{ actor_kind?: string, actor?: string }>({
    save: async (item) => {
      setActorsFilter(item);
      return item;
    }
  });

  const heroes2 = heroes || EmptyStrings;

  const schema: Schema = useMemo(() => {
    return mergeSchema(
      ActorsFilterSchema, {
        actor: editActorsFilter.item?.actor_kind === "support"
          ? { type: FieldType.dictionarySelect, dictionary: "ProjectSupportActors", label: "Кто" }
          : createSelectSchema(heroes2.map(h => ({ value: h, label: h })),
            { label: "Кто" }
          )
      }
    )
  }, [editActorsFilter.item?.actor_kind, heroes2]);

  return {
    current: actorsFilter,
    clear: () => {
      setActorsFilter(null);
      editActorsFilter.cancel();
    },
    edit: {
      ...editActorsFilter,
      startEditing: () => {
        editActorsFilter.startEditing(actorsFilter || { actor_kind: "hero" })
      }
    },
    schema,
  }
}

export const useProjectsListBase = (mode?: "process" | "project") => {
  const { schema } = useSingleSchema(`${ProjectApiPath}/uiconfig`);
  const { schema: stageSchema } = useSingleSchema(`${ProjectStageApiPath}/uiconfig`);
  const { schema: statusSchemaRaw } = useSingleSchema(`${ProjectStatusApiPath}/uiconfig`);

  const statusDates = useStatusDates(mode || "project");

  const payAttentionCount = useLoadedData<{ total_records: number }>(`${ProjectApiPath}/count?states=pay-attention&record-type=${mode || "project"}`, { total_records: 0 })

  const statusSchema = useMemo(() => {
    return mergeSchema(statusSchemaRaw, {
      status_date: statusDates.fieldSchema,
    })
  }, [statusDates.fieldSchema, statusSchemaRaw]);

  const [curators,setCurators] = useState<string[]>([]);

  const stateFilter = useBrowserStoredValue("unfinished", undefined, "__p_states");
  const sorting = useBrowserStoredValue("status", undefined, "__p_sorting");
  const stateFilterOptions = useStateFilterOptions();

  const projectStates = useProjectStates();

  const data = useSmartList<Project>(`${ProjectApiPath}`, {
    newRecordDefault: { code: "", title: "", state: projectStates.defaultProjectState?.code, ...(mode === "process" ? { record_type: "process" } : {}) },
    schema,
    noLimit: true,
    textFilterFn: p => `${p.code} ${p.title}`,
    getRecordApiPath: p => `${ProjectApiPath}/${p._id}`,
    extraParams: {
      curators,
      sorting: sorting.value,
      completed: "include",
      states: stateFilterOptions.find(o => o.code === stateFilter.value)?.states || "",
      ...(mode === "process" ? { "record-type": "process" } : {})
    }
  });

  const autosuggestions = useAutoSuggestionsStore();
  const curatorsOptions = useLoadedData<{ projects: string[], processes: string[], all: string[]}>("/api/project/curators-new", { projects: [], processes: [], all: []});
  useEffect(() => {
    autosuggestions.setOptions(CuratorsSuggestionsKey, curatorsOptions.data.all || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curatorsOptions.data])


  const membersOptions = useLoadedData<string[]>("/api/project/members", []);
  useEffect(() => {
    autosuggestions.setOptions(MembersSuggestionsKey, membersOptions.data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersOptions.data]);

  const actorsFilter = useActorsFilter(membersOptions.data);

  const filteredData = useMemo(() => {
    if(!actorsFilter.current) {
      return data.data;
    } else {
      return data.data.filter(p => {
        if(actorsFilter.current?.actor_kind === "support") {
          return p.support_needed?.includes(actorsFilter.current?.actor as string);
        } else if(actorsFilter.current?.actor_kind === "hero") {
          return p.heroes?.includes(actorsFilter.current?.actor as string);
        }
        return false;
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.data, actorsFilter.current]);

  const [closeEditTrigger,setCloseEditTrigger] = useState<string>("no");
  useEffect(() => {
    if(data.editRecord.dialog.isOpen) {
      setCloseEditTrigger("prep");
    } else {
      setCloseEditTrigger(x => x === "prep" ? "yes" : "no");
    }
  }, [data.editRecord.dialog.isOpen]);
  useEffect(() => {
    if(closeEditTrigger === "yes" && !data.isLoading) {
      data.reload();
      setCloseEditTrigger("no")
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeEditTrigger, data.isLoading])

  const editStatusCurrent = useEditItem2<ProjectStatusEdit>({
    save: async (item) => {
      let saved: ProjectStatus | null = null;
      if(item._id < 0) {
        saved = await apiFetch<ProjectStatus>(`/api/project/status`, "post", {
          project_id: item.project_id,
          description: item.description,
          status_date: item.status_date,
          alert: item.alert || false,
        });
      } else {
        saved = await apiFetch<ProjectStatus>(`/api/project/status/${item._id}`, "put", {
          description: item.description,
          alert: item.alert || false,
        });
      }
      data.reload();
      payAttentionCount.reload();
      if(data.editRecord.item && data.editRecord.item._id === item.project_id) {
        if(item.which === "current") {
          data.editRecord.item.project_status = { ...saved };
        } else {
          data.editRecord.item.project_status_prev = { ...saved };
        }
      }
      return item;
    }
  });

  const overloadedMembers = useMemo(() => {
    const heroesCounts = data.data.reduce<Record<string,number>>((r,p) => {
      const pStatus = projectStates.byCode[p.state];
      if(pStatus?.extra?.is_inprogress || pStatus?.extra?.is_focus) {
        p.heroes?.forEach(h => {
          r[h.toUpperCase()] = (r[h.toUpperCase()] || 0) + 1;
        })
      }
      return r;
    }, {});

    return Object.entries(heroesCounts).filter(([h,c]) => c >= OverloadedMemberHeroCount).map(([h,c]) => h);
  }, [data.data, projectStates.byCode]);

  return {
    ...data,
    data: filteredData,
    stageSchema,
    statusSchema,
    extraFilters: {
      curators,
      curatorsOptions: mode === "process" ? curatorsOptions.data.processes : curatorsOptions.data.projects,
      setCurators,
      states: stateFilter,
      statesOptions: stateFilterOptions,
      statesLabel: stateFilterOptions.find(o => o.code === stateFilter.value)?.label || "...",
      sorting,
      sortingOptions: SortingOptions,

      actors: actorsFilter,
    },
    overloadedMembers,

    editStatus: {
      ...editStatusCurrent,
      start: (project: Project) => {
        editStatusCurrent.startEditing({
          ...(project.project_status || { _id: -1, project_id: project._id, description: "", status_date: statusDates.dates.current.date } as any ),
          previous: project.project_status_prev,
          previous_description: project.project_status_prev?.description,
          status_date_label: statusDates.dates.current.label,
          status_prev_date_label: statusDates.dates.prev.label,
          project_title: project.code,
          which: "current",
        })
      },

      startPrev: (project: Project) => {
        editStatusCurrent.startEditing({
          ...(project.project_status_prev || { _id: -1, project_id: project._id, description: "", status_date: statusDates.dates.prev.date } as any ),
          previous_description: "",
          status_date_label: statusDates.dates.prev.label,
          status_prev_date_label: "",
          project_title: project.code,
          which: "prev",
        })
      }
    },

    mode: mode || "project",
    payAttentionCount,
  };
}

export type ProjectsListData = ReturnType<typeof useProjectsListBase>;

export const useProjectsList = () => {
  return useProjectsListBase();
}


export const useProcessList = () => {
  return useProjectsListBase("process");
}
